/**
 * K-1 (Schedule K-1, Form 1065) editable form.
 *
 * A file is a K-1 when `file.is_k1 === true`; its form is read from the dedicated
 * `GET /files/{id}/extraction/k1-tags` endpoint and saved via `POST /files/{id}/extraction/k1`.
 *
 * Contract: k1-form-api.md (repo root).
 */

export type K1FieldType = "number" | "text" | "boolean";

/** Native boolean values the form uses on the wire when saving a checkbox field. */
export const K1_BOOLEAN_CHECKED = "Checked";
export const K1_BOOLEAN_UNCHECKED = "Unchecked";

/** One row of a multi-value (`is_multiple`) field — a code `key` + its `value`. */
export interface IK1MultiValue {
  /** Row code (e.g. "A", "B", "Z"). Required when saving; user-editable. */
  key: string;
  value: string;
}

/** A field's value: scalar string, boolean (checkboxes), or rows (`is_multiple`). */
export type IK1FieldValue = string | boolean | IK1MultiValue[];

export interface IK1Field {
  /** Opaque identifier — echo verbatim when saving; never hand-construct. */
  key: string;
  /** Human label (box prefix already stripped). */
  name: string;
  /** Box code shared by every field in the group ("L", "9c", "J", "1"…). */
  category_number: string;
  type: K1FieldType;
  /** true → `value` is an array of `{ key, value }` rows; false/absent → scalar. */
  is_multiple?: boolean;
  /** boolean for checkboxes; `IK1MultiValue[]` when `is_multiple`; otherwise a (possibly empty) string. */
  value: IK1FieldValue;
  /** Present for percentages ("%"); render as a suffix on the value. */
  postfix?: string;
  /** true = user edited this field (differs from the extraction). */
  is_manually_changed: boolean;
}

/** A single box on the form; may hold multiple fields (e.g. box J, box L). */
export interface IK1Group {
  category_number: string;
  fields: IK1Field[];
}

export type K1PartId = "part1" | "part2" | "part3" | "ungrouped";

export interface IK1Part {
  part: K1PartId;
  /** Display heading. */
  title: string;
  groups: IK1Group[];
}

export interface IK1Form {
  file_id: number;
  tax_year: number;
  parts: IK1Part[];
}

/**
 * POST /files/{id}/extraction/k1 body — keyed by field `key`. Scalar fields send a string;
 * `is_multiple` fields send their `{ key, value }` rows (each row's `key` required).
 */
export interface IK1SaveData {
  fields: Record<string, string | IK1MultiValue[]>;
}
